import React, { useEffect, useState } from "react";
import { Layout, Table, Button, Modal, Form, Input, Select, message } from "antd";
import { getAllMusicTypes,
    createMusicType,
    updateMusicType,
    deleteMusicType,
    createSubsection,
    updateSubsection,
    deleteSubsection, } from "@/server/news/NewsServer";

const { Sider, Content } = Layout;
const { Option } = Select;

const FilterPage = () => {
  const [musicTypes, setMusicTypes] = useState([]);
  const [selectedMusicType, setSelectedMusicType] = useState(null);
  const [isMusicTypeModalVisible, setIsMusicTypeModalVisible] = useState(false);
  const [isSubsectionModalVisible, setIsSubsectionModalVisible] = useState(false);
  const [editingMusicType, setEditingMusicType] = useState(null);
  const [editingSubsection, setEditingSubsection] = useState(null);
  const [musicTypeForm] = Form.useForm();
  const [subsectionForm] = Form.useForm();

  // Fetch music types
  useEffect(() => {
    fetchMusicTypes();
  }, []);

  const fetchMusicTypes = async () => {
    try {
      const response = await getAllMusicTypes();
      setMusicTypes(response.data);
    } catch (error) {
      message.error("Failed to fetch music types");
    }
  };

  // Handle music type selection
  const handleMusicTypeClick = (musicType) => {
    setSelectedMusicType(musicType);
  };

  // Handle music type creation/update
  const handleMusicTypeSubmit = async (values) => {
    try {
      if (editingMusicType) {
        await updateMusicType(editingMusicType._id, values);
        message.success("Music type updated successfully");
      } else {
        await createMusicType(values);
        message.success("Music type created successfully");
      }
      setIsMusicTypeModalVisible(false);
      setEditingMusicType(null);
      fetchMusicTypes();
    } catch (error) {
      message.error("Failed to save music type");
    }
  };

  // Handle music type deletion
  const handleDeleteMusicType = async (id) => {
    try {
      await deleteMusicType(id);
      message.success("Music type deleted successfully");
      fetchMusicTypes();
      setSelectedMusicType(null); // Clear selected music type if deleted
    } catch (error) {
      message.error("Failed to delete music type");
    }
  };

  // Handle subsection creation/update
  const handleSubsectionSubmit = async (values) => {
    try {
      if (editingSubsection) {
        await updateSubsection(editingSubsection._id, values);
        message.success("Subsection updated successfully");
      } else {
        await createSubsection(values);
        message.success("Subsection created successfully");
      }
      setIsSubsectionModalVisible(false);
      setEditingSubsection(null);
      fetchMusicTypes(); // Refresh music types to update subsections
    } catch (error) {
      message.error("Failed to save subsection");
    }
  };

  // Handle subsection deletion
  const handleDeleteSubsection = async (id) => {
    try {
      await deleteSubsection(id);
      message.success("Subsection deleted successfully");
      fetchMusicTypes(); // Refresh music types to update subsections
    } catch (error) {
      message.error("Failed to delete subsection");
    }
  };

  // Open modal for editing music type
  const handleEditMusicType = (musicType) => {
    setEditingMusicType(musicType);
    musicTypeForm.setFieldsValue(musicType);
    setIsMusicTypeModalVisible(true);
  };

  // Open modal for editing subsection
  const handleEditSubsection = (subsection) => {
    setEditingSubsection(subsection);
    subsectionForm.setFieldsValue(subsection);
    setIsSubsectionModalVisible(true);
  };

  // Filter subsections by selected music type
  const filteredSubsections = selectedMusicType?.subSections || [];

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {/* Left Sidebar for Music Types */}
      <Sider width={400} style={{ background: "#fff", padding: "20px" }}>
        {/* <Button
          type="primary"
          style={{ marginBottom: "20px", width: "100%" }}
          onClick={() => {
            setEditingMusicType(null);
            musicTypeForm.resetFields();
            setIsMusicTypeModalVisible(true);
          }}
        >
          Add Music Type
        </Button> */}
        <Table
          dataSource={musicTypes}
          columns={[
            { title: "Name", dataIndex: "value", key: "value" },
            // {
            //   title: "Actions",
            //   key: "actions",
            //   render: (_, record) => (
            //     <>
            //       <Button
            //         type="link"
            //         onClick={(e) => {
            //           e.stopPropagation();
            //           handleEditMusicType(record);
            //         }}
            //       >
            //         Edit
            //       </Button>
            //       <Button
            //         type="link"
            //         danger
            //         onClick={(e) => {
            //           e.stopPropagation();
            //           handleDeleteMusicType(record._id);
            //         }}
            //       >
            //         Delete
            //       </Button>
            //     </>
            //   ),
            // },
          ]}
          onRow={(record) => ({
            onClick: () => handleMusicTypeClick(record),
          })}
          rowKey="_id"
          pagination={false}
        />
      </Sider>

      {/* Main Content for Subsections */}
      <Content style={{ padding: "20px" }}>
        {selectedMusicType ? (
          <>
            <h3>Subsections for {selectedMusicType.value}</h3>
            <Button
              type="primary"
              style={{ marginBottom: "20px" }}
              onClick={() => {
                setEditingSubsection(null);
                subsectionForm.resetFields();
                setIsSubsectionModalVisible(true);
              }}
            >
              Add Subsection
            </Button>
            <Table
              dataSource={filteredSubsections}
              columns={[
                { title: "Name", dataIndex: "name", key: "name" },
                { title: "Discovery Page", dataIndex: "discoveryPage", key: "discoveryPage" },
                {
                  title: "Actions",
                  key: "actions",
                  render: (_, record) => (
                    <>
                      <Button onClick={() => handleEditSubsection(record)}>Edit</Button>
                      <Button
                        danger
                        onClick={() => handleDeleteSubsection(record._id)}
                        style={{ marginLeft: "8px" }}
                      >
                        Delete
                      </Button>
                    </>
                  ),
                },
              ]}
              rowKey="_id"
            />
          </>
        ) : (
          <h2>Please select a music type to view subsections.</h2>
        )}
      </Content>

      {/* Modals for CRUD Operations */}
      <Modal
        title={editingMusicType ? "Edit Music Type" : "Add Music Type"}
        visible={isMusicTypeModalVisible}
        onCancel={() => setIsMusicTypeModalVisible(false)}
        onOk={() => musicTypeForm.submit()}
      >
        <Form form={musicTypeForm} onFinish={handleMusicTypeSubmit}>
          <Form.Item name="value" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingSubsection ? "Edit Subsection" : "Add Subsection"}
        visible={isSubsectionModalVisible}
        onCancel={() => setIsSubsectionModalVisible(false)}
        onOk={() => subsectionForm.submit()}
      >
        <Form form={subsectionForm} onFinish={handleSubsectionSubmit}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="discoveryPage" label="Discovery Page" rules={[{ required: true }]}>
            <Select>
              <Option value="Artist">Artist</Option>
              <Option value="Event">Event</Option>
              <Option value="Music">Music</Option>
              <Option value="magazine">Magazine</Option>
              <Option value="pragency">Pragency</Option>
              <Option value="talentmanager">Talent manager</Option>
              <Option value="LABEL">Label</Option>
              <Option value="eventmanager">Event manager</Option>
            </Select>
          </Form.Item>
          <Form.Item name="musicType" label="Music Type" rules={[{ required: true }]}>
            <Select>
              {musicTypes.map((musicType) => (
                <Option key={musicType._id} value={musicType._id}>
                  {musicType.value}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default FilterPage;